import * as Sentry from '@sentry/react';

export type SentryConfig = {
  dsn?: string;
  environment: string;
  release?: string;
  isDev: boolean;
};

export const shouldEnableSentry = (dsn: string | undefined, isDev: boolean) =>
  Boolean(dsn) && !isDev;

export const frontendTracesSampleRate = (environment: string) =>
  environment === 'production' ? 0.1 : 0.2;

export const frontendReplaySampleRate = (environment: string) =>
  environment === 'production' ? 0.01 : 0;

export const frontendReplayOnErrorSampleRate = (environment: string) =>
  environment === 'production' ? 0.1 : 0.2;

type SentryEventLike = {
  exception?: {
    values?: Array<{
      type?: string;
      stacktrace?: {
        frames?: Array<{
          abs_path?: string;
          filename?: string;
          module?: string;
        }>;
      };
    }>;
  };
};

const GOOGLE_MAPS_SCRIPT_PATH = 'maps.googleapis.com/maps/api/js';

export const isGoogleMapsUriErrorEvent = (event: SentryEventLike) =>
  event.exception?.values?.some(
    (exception) =>
      exception.type === 'URIError' &&
      exception.stacktrace?.frames?.some((frame) =>
        [frame.abs_path, frame.filename, frame.module]
          .filter(Boolean)
          .some((value) => value?.includes(GOOGLE_MAPS_SCRIPT_PATH) || value === '/maps/api/js')
      )
  ) ?? false;

export const initializeSentry = ({ dsn, environment, release, isDev }: SentryConfig) => {
  if (!shouldEnableSentry(dsn, isDev)) {
    return false;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    enabled: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    sendDefaultPii: false,
    tracesSampleRate: frontendTracesSampleRate(environment),
    replaysSessionSampleRate: frontendReplaySampleRate(environment),
    replaysOnErrorSampleRate: frontendReplayOnErrorSampleRate(environment),
    beforeSend(event) {
      if (isGoogleMapsUriErrorEvent(event)) {
        return null;
      }

      return event;
    },
  });

  return true;
};

export const captureFrontendException = (error: unknown, extra?: Record<string, unknown>) => {
  Sentry.captureException(error, extra ? { extra } : undefined);
};
